// A01: Connected Repos panel inside Workspace settings.
// - "Connect GitHub" redirects to the backend OAuth start route.
// - Once connected, lists the Captain's authorised repos via
//   /github/repos?workspace_id=... and lets them multi-select +
//   attach them to this workspace.
// - Already-attached repos render in a second list with a detach
//   button.

import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import {
  ApiError,
  attachRepo,
  connectGithubToken,
  detachRepo,
  extractRows,
  listGithubRepos,
  startGithubOauth,
  listWorkspaceRepoConnections,
  type GithubRepo,
} from "../api/client";
import { rowToRepoConnection } from "./rowParsers";
import type { RepoConnection } from "./types";
import { openGithubOauthPopup } from "./githubOauthPopup";

interface Props {
  workspaceId: number;
}

export function ConnectedReposPanel({ workspaceId }: Props) {
  const [connections, setConnections] = useState<RepoConnection[]>([]);
  const [availableRepos, setAvailableRepos] = useState<GithubRepo[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [githubConfigured, setGithubConfigured] = useState<boolean | null>(null);
  const [tokenOpen, setTokenOpen] = useState(false);
  const [tokenValue, setTokenValue] = useState("");
  const [tokenBusy, setTokenBusy] = useState(false);
  const [tokenInfo, setTokenInfo] = useState("");

  async function refreshConnections() {
    try {
      const res = await listWorkspaceRepoConnections(workspaceId);
      const list = extractRows(res)
        .map(rowToRepoConnection)
        .filter((r): r is RepoConnection => r !== null);
      setConnections(list);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not load connected repos",
      );
    }
  }

  async function openPicker() {
    setError("");
    setBusy(true);
    try {
      const repos = await listGithubRepos(workspaceId);
      setAvailableRepos(repos);
      setGithubConfigured(true);
      setShowPicker(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        // no_github_token — Captain hasn't connected GitHub for this org yet.
        setGithubConfigured(false);
      } else {
        setError(
          err instanceof ApiError
            ? err.message
            : "Could not list GitHub repos",
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function startOauth() {
    setError("");
    try {
      const { url } = await startGithubOauth(workspaceId);
      const result = await openGithubOauthPopup(url, workspaceId);
      if (result.ok) {
        setGithubConfigured(true);
        await openPicker();
      } else if (result.reason === "closed") {
        setError("OAuth popup was closed before authorization completed.");
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 503 && /OAUTH/.test(err.message)) {
        setTokenOpen(true);
        setError("GitHub OAuth is not configured here yet. Paste a token below to keep moving.");
      } else {
        setError(err instanceof ApiError ? err.message : "Could not start GitHub OAuth");
      }
    }
  }

  async function saveToken() {
    if (!tokenValue.trim()) return;
    setTokenBusy(true);
    setError("");
    setTokenInfo("");
    try {
      const saved = await connectGithubToken(workspaceId, tokenValue.trim());
      setTokenValue("");
      setTokenOpen(false);
      setGithubConfigured(true);
      setTokenInfo(`GitHub connected as ${saved.login}.`);
      await openPicker();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save GitHub token");
    } finally {
      setTokenBusy(false);
    }
  }

  async function onAttach(repo: GithubRepo) {
    setError("");
    setBusy(true);
    try {
      const [owner, name] = repo.full_name.split("/");
      await attachRepo(
        workspaceId,
        owner,
        name,
        repo.default_branch || "main",
      );
      await refreshConnections();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not attach repo",
      );
    } finally {
      setBusy(false);
    }
  }

  async function onDetach(c: RepoConnection) {
    setError("");
    setBusy(true);
    try {
      await detachRepo(c.repo_connection_id);
      await refreshConnections();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not detach repo",
      );
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (workspaceId > 0) void refreshConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const attachedKeys = new Set(
    connections.map((c) => `${c.github_owner}/${c.github_repo}`),
  );

  return (
    <section
      className="flex flex-col gap-3 rounded-panel border border-edge bg-surface-glass p-4"
      data-testid="connected-repos-panel"
    >
      <header className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-primary">
            Connected repos
          </h3>
          <p className="mt-1 text-xs text-muted">
            GitHub repositories this workspace can clone into its sandbox.
          </p>
        </div>
        {githubConfigured === false ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void startOauth()}
              className="flex cursor-pointer items-center gap-2 rounded-md border border-edge bg-surface-raised px-3 py-1.5 text-xs text-primary transition-colors hover:border-accent"
              data-testid="connect-github"
            >
              Connect GitHub →
            </button>
            <button
              type="button"
              onClick={() => setTokenOpen((v) => !v)}
              className="rounded-md border border-edge px-3 py-1.5 text-xs text-muted transition-colors hover:border-accent hover:text-primary"
            >
              Use token
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => (showPicker ? setShowPicker(false) : openPicker())}
            disabled={busy}
            className="flex cursor-pointer items-center gap-2 rounded-md border border-edge bg-surface-raised px-3 py-1.5 text-xs text-primary transition-colors hover:border-accent disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="attach-repo-toggle"
          >
            <Plus size={14} />
            {showPicker ? "Close" : "Attach repo"}
          </button>
        )}
      </header>

      {tokenOpen && (
        <div className="flex items-center gap-2 rounded-md border border-edge bg-surface-raised p-2">
          <input
            type="password"
            value={tokenValue}
            onChange={(e) => setTokenValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void saveToken();
              }
            }}
            placeholder="github_pat_..."
            autoComplete="off"
            className="min-w-0 flex-1 rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary placeholder:text-faint"
            data-testid="connected-repos-github-token"
          />
          <button
            type="button"
            onClick={() => void saveToken()}
            disabled={tokenBusy || !tokenValue.trim()}
            className="rounded-md border border-accent px-3 py-1 text-xs font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
          >
            {tokenBusy ? "Saving..." : "Save"}
          </button>
        </div>
      )}

      {tokenInfo && <p className="text-xs text-success">{tokenInfo}</p>}

      {connections.length === 0 ? (
        <p className="text-xs text-faint">
          No repos connected yet.
        </p>
      ) : (
        <ul
          className="flex flex-col gap-1"
          data-testid="connected-repos-list"
        >
          {connections.map((c) => (
            <li
              key={c.repo_connection_id}
              className="flex items-center justify-between rounded-md border border-edge bg-surface-raised px-3 py-2 text-xs"
            >
              <span className="text-primary">
                {c.github_owner}/<span className="font-medium">{c.github_repo}</span>
                <span className="ml-2 text-faint">@{c.default_branch}</span>
              </span>
              <button
                type="button"
                onClick={() => void onDetach(c)}
                disabled={busy}
                aria-label={`Detach ${c.github_owner}/${c.github_repo}`}
                className="cursor-pointer text-faint transition-colors hover:text-danger disabled:cursor-not-allowed"
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {showPicker && availableRepos.length > 0 && (
        <div
          className="flex flex-col gap-1 rounded-md border border-edge bg-surface-raised p-2"
          data-testid="repo-picker"
        >
          <p className="px-1 py-1 text-[0.6875rem] text-muted">
            Your GitHub repos (most recently updated first):
          </p>
          <ul className="flex max-h-60 flex-col gap-0.5 overflow-y-auto">
            {availableRepos.map((repo) => {
              const already = attachedKeys.has(repo.full_name);
              return (
                <li key={repo.id}>
                  <button
                    type="button"
                    onClick={() => !already && void onAttach(repo)}
                    disabled={already || busy}
                    className="flex w-full cursor-pointer items-center justify-between rounded px-2 py-1 text-left text-xs text-primary transition-colors hover:bg-surface-glass disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <span>
                      {repo.full_name}
                      <span className="ml-2 text-faint">
                        @{repo.default_branch || "main"}
                      </span>
                      {repo.private && (
                        <span className="ml-2 text-faint">(private)</span>
                      )}
                    </span>
                    {already && (
                      <span className="text-[0.625rem] text-faint">
                        attached
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {error && (
        <p className="text-xs text-danger" data-testid="connected-repos-error">
          {error}
        </p>
      )}
    </section>
  );
}
